/**
 * Where somebody was standing — the one piece of the map that is now a save file.
 *
 * The map used to be a view and nothing else: `Mapa` recomputed a spawn on every mount from the
 * member's next step, and `onMove` carried a comment saying movement is deliberately not persisted.
 * That is a defensible design right up until a member walks out to the fourth island, opens a task
 * card, closes it, and is standing back on PLIĆAK — because `nextStep` answers "where is your next
 * task", which for most of this community is an earlier island than the one they are exploring.
 * So position is remembered now, and this module is the vocabulary for it.
 *
 * **It is stored as an offset from the island's authored centre, never as a map coordinate.** That
 * is the whole reason this file exists rather than two integers on a row. `buildWorld` lays the
 * archipelago out starting from the islands *the viewer can see*, so its origin slides the moment a
 * new island appears — a raw coordinate saved on Tuesday means a different patch of sea on Thursday,
 * silently, with no version to notice it by. The island's centre does not move: it is authored
 * content, identical for everybody, and both the generated ellipse and a drawn canvas are centred on
 * it. `core/soba.ts` already reached this conclusion for the live wire and `toOffset`/`fromOffset`
 * live there; this is the same measurement, kept instead of relayed.
 *
 * Pure, and shared by the browser and the Worker, because the endpoint has to refuse exactly what
 * the client would never send.
 */

import { FACINGS, MAX_OFFSET, type Facing } from './soba';

/**
 * A remembered position: which island, how far from its centre, and which way they were looking.
 *
 * Serbian keys, because this crosses the wire and the wire's vocabulary in this codebase is
 * whatever the feature is called in the product. `f` is a `FACINGS` index rather than a name for
 * the same reason the live protocol packs it that way — one byte, one fixed order, already frozen.
 */
export interface Mesto {
  ostrvo: string;
  dx: number;
  dy: number;
  f: number;
}

/**
 * Island ids come from content and are already constrained at the publish gate (`contentId` in
 * `core/content.ts`). This is the storage-side backstop: the value arrives as JSON from a browser,
 * and the only thing that ever reads it back is a `Map.get`, so the bound that matters is length.
 */
const MAX_ISLAND_ID = 64;

const isFiniteInt = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value);

/**
 * Parse a remembered position, or null.
 *
 * Null rather than a clamp, deliberately: a position that has to be corrected is a position nobody
 * chose, and the caller already has a better answer than a corrected one — the island's dock, or the
 * next step. Clamping would put a member somewhere they have never been and call it a restore.
 */
export function readMesto(value: unknown): Mesto | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const ostrvo = raw.ostrvo;
  if (typeof ostrvo !== 'string' || ostrvo.length === 0 || ostrvo.length > MAX_ISLAND_ID) return null;
  const { dx, dy, f } = raw;
  if (!isFiniteInt(dx) || !isFiniteInt(dy) || !isFiniteInt(f)) return null;
  if (Math.abs(dx) > MAX_OFFSET || Math.abs(dy) > MAX_OFFSET) return null;
  if (f < 0 || f >= FACINGS.length) return null;
  return { ostrvo, dx, dy, f };
}

export const facingOfIndex = (index: number): Facing => FACINGS[index] ?? 'down';
export const indexOfFacing = (facing: Facing): number => Math.max(0, FACINGS.indexOf(facing));

/** True when two records describe the same tile and the same direction — i.e. nothing to save. */
export function sameMesto(a: Mesto | null, b: Mesto | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.ostrvo === b.ostrvo && a.dx === b.dx && a.dy === b.dy && a.f === b.f;
}
